import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, ArrowDown } from "lucide-react";
import { EASE } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const HERO_IMG =
  "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxhaSUyMG5ldHdvcmslMjBhYnN0cmFjdHxlbnwwfHx8fDE3OTE1OTA2NzN8MA&ixlib=rb-4.1.0&q=85";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.15, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative flex min-h-screen items-center overflow-hidden pb-24 pt-36 lg:pt-40">
      <motion.div className="absolute inset-0" style={{ y: bgY }} aria-hidden="true">
        <img
          src={HERO_IMG}
          alt=""
          className="h-full w-full object-cover opacity-[0.14]"
          style={{
            maskImage: "radial-gradient(ellipse 90% 70% at 60% 30%, black 30%, transparent 78%)",
            WebkitMaskImage: "radial-gradient(ellipse 90% 70% at 60% 30%, black 30%, transparent 78%)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-transparent to-ink" />
      </motion.div>

      <motion.div style={{ opacity: contentOpacity }} className="relative z-10 mx-auto w-full max-w-7xl px-6 lg:px-10">
        <div className="grid items-center gap-16 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-9">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-[10px] uppercase tracking-[0.28em] text-gold sm:text-[11px]"
            >
              <span className="h-px w-10 bg-gold" aria-hidden="true" />
              AI Governance · Cybersecurity · Enterprise Risk · PMO &amp; Transformation
            </motion.p>

            <h1 className="mt-8 font-display text-[clamp(3rem,7vw,6.4rem)] leading-[1.04] tracking-wide">
              <MaskedLine delay={0.25}>
                Govern with <span className="gold-text">confidence.</span>
              </MaskedLine>
              <MaskedLine delay={0.42}>
                Transform with <span className="text-outline">purpose.</span>
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
              className="mt-8 max-w-xl text-base font-light leading-relaxed text-slate-300 sm:text-lg"
            >
              AiCendor puts certified governance around your AI, protection around what
              matters, opportunity around your risk and precision around your delivery —
              so innovation never outruns control.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
              className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
            >
              <button
                data-testid="primary-cta-button"
                onClick={() => scrollToId("book")}
                className="btn-sweep btn-sweep-light border border-gold bg-gold px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.22em] text-ink"
              >
                Book a consultation call
              </button>
              <button
                data-testid="secondary-cta-button"
                onClick={() => scrollToId("services")}
                className="btn-sweep border border-white/25 px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-white"
              >
                Explore capabilities
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.15 }}
              className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400"
            >
              <span className="flex items-center gap-2">
                <ShieldCheck className="h-3.5 w-3.5 text-gold" /> AIGP-certified leadership
              </span>
              <span className="hidden h-1 w-1 rotate-45 bg-gold sm:inline-block" aria-hidden="true" />
              <span>Enterprise &amp; mid-market</span>
              <span className="hidden h-1 w-1 rotate-45 bg-gold sm:inline-block" aria-hidden="true" />
              <span>Global delivery</span>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1.4, delay: 1.3, ease: EASE }}
          className="hairline-x mt-16 h-px w-full origin-left"
          aria-hidden="true"
        />
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("services")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-3 text-slate-500 transition-colors hover:text-gold md:flex"
        aria-label="Scroll to services"
      >
        <span className="font-mono text-[9px] uppercase tracking-[0.35em]">Scroll</span>
        <ArrowDown className="h-4 w-4 animate-bounce" />
      </motion.button>
    </section>
  );
}
